/**
 * MyChecklist — employee-facing onboarding checklist rendered on the
 * My Access page. Auto-resolves tour-watch + library-read items from
 * the backend, lets the user click "I've done this" on `acknowledge`
 * items, and shows greyed-out HR-marked items the user can't toggle.
 */
import { useEffect, useState } from "react";
import { CheckCircle, Circle, ClipboardText, Lightbulb, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchMyChecklist, ackMyChecklistItem } from "@/lib/api";

const TYPE_LABEL = {
  library_read: "Read",
  tour_watch:   "Tour",
  acknowledge:  "Self-mark",
  hr_marked:    "HR-marked",
};

export default function MyChecklist() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    fetchMyChecklist().then(setData).catch(() => setData({ has_assignment: false }));
  }, []);

  if (!data) return null;
  if (!data.has_assignment) return null;

  const ack = async (item_id) => {
    setBusy(item_id);
    try {
      const fresh = await ackMyChecklistItem(item_id);
      setData(fresh);
      toast.success("Marked done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark item");
    } finally { setBusy(""); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5"
             data-testid="my-checklist">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardText size={16} weight="bold" className="text-indigo-600" />
        <h2 className="text-sm font-bold font-['Chivo']">Your onboarding checklist</h2>
        <span className="ml-auto text-[11px] font-mono text-slate-500"
              data-testid="my-checklist-progress-text">
          {data.completed_count} / {data.total} done · {data.percent}%
        </span>
      </div>
      {data.template_name && (
        <p className="text-[11px] text-slate-500 mb-3">{data.template_name}</p>
      )}

      <div className="h-1.5 w-full rounded-full bg-slate-200 overflow-hidden mb-3">
        <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 transition-[width] duration-500"
             data-testid="my-checklist-progress-bar"
             style={{ width: `${data.percent}%` }} />
      </div>

      <ul className="space-y-1">
        {data.items.map((it) => {
          const Icon = it.completed ? CheckCircle : (it.type === "hr_marked" ? Lock : Circle);
          return (
            <li key={it.item_id}
                data-testid={`my-checklist-item-${it.item_id}`}
                className={`flex items-start gap-2 p-2 rounded-sm border ${
                  it.completed ? "bg-emerald-50 border-emerald-200"
                  : it.type === "hr_marked" ? "bg-slate-50 border-slate-200"
                  : "bg-white border-slate-200"
                }`}>
              <Icon size={14} weight={it.completed ? "fill" : "regular"}
                    className={it.completed ? "text-emerald-600 mt-0.5" : "text-slate-400 mt-0.5"} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-slate-800">{it.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {TYPE_LABEL[it.type] || it.type}
                  {it.completed_at && ` · ${(it.completed_at || "").slice(0, 10)}`}
                </div>
              </div>
              {!it.completed && it.type === "acknowledge" && (
                <button data-testid={`my-checklist-ack-${it.item_id}`}
                        onClick={() => ack(it.item_id)} disabled={busy === it.item_id}
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                  I've done this
                </button>
              )}
              {!it.completed && it.type === "hr_marked" && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  HR will mark
                </span>
              )}
              {!it.completed && (it.type === "tour_watch" || it.type === "library_read") && (
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 inline-flex items-center gap-1">
                  <Lightbulb size={10} weight="fill" /> Auto-tracks
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
